/**
 * 冒烟测试：模拟真实前端点击 / 等待 / 填表（可见高亮动画）。
 */

export function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

export function errorMessage(err: unknown): string {
  if (err == null) return '未知错误';
  if (typeof err === 'string') return err;
  if (err instanceof Error && err.message && err.message !== '[object Object]') {
    return err.message;
  }
  const anyErr = err as {
    message?: unknown;
    data?: { message?: unknown; error?: unknown };
    response?: { data?: { message?: unknown } };
  };
  const candidates = [
    anyErr?.data?.message,
    anyErr?.data?.error,
    anyErr?.response?.data?.message,
    anyErr?.message,
  ];
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim() && c !== '[object Object]') return c.trim();
  }
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

export async function waitUntil(
  predicate: () => boolean | Promise<boolean>,
  opts?: { timeoutMs?: number; intervalMs?: number; label?: string; onTick?: () => Promise<void> | void },
): Promise<void> {
  const timeoutMs = opts?.timeoutMs ?? 120_000;
  const intervalMs = opts?.intervalMs ?? 400;
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    if (opts?.onTick) await opts.onTick();
    if (await predicate()) return;
    await sleep(intervalMs);
  }
  throw new Error(`等待超时：${opts?.label || '条件未满足'}（>${Math.round(timeoutMs / 1000)}s）`);
}

/** 查找可见元素（优先 data-smoke，其次文本/选择器） */
export function queryVisible(selector: string, root: ParentNode = document): HTMLElement | null {
  const list = Array.from(root.querySelectorAll(selector)) as HTMLElement[];
  return list.find((el) => el.offsetParent !== null || el.getClientRects().length > 0) || list[0] || null;
}

export function findByText(
  selector: string,
  text: string | RegExp,
  root: ParentNode = document,
): HTMLElement | null {
  const nodes = Array.from(root.querySelectorAll(selector)) as HTMLElement[];
  return (
    nodes.find((el) => {
      const t = (el.innerText || el.textContent || '').replace(/\s+/g, ' ').trim();
      if (typeof text === 'string') return t === text || t.includes(text);
      return text.test(t);
    }) || null
  );
}

export async function highlightAndClick(
  el: Element,
  opts?: { settleMs?: number; pulseMs?: number },
): Promise<void> {
  const html = el as HTMLElement;
  html.setAttribute('data-smoke-driving', '1');
  html.classList.add('smoke-drive-pulse');
  try {
    html.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  } catch {
    /* ignore */
  }
  await sleep(opts?.pulseMs ?? 450);
  html.focus?.();
  html.click();
  await sleep(opts?.settleMs ?? 350);
  html.classList.remove('smoke-drive-pulse');
  html.removeAttribute('data-smoke-driving');
}

export async function clickSelector(
  selector: string,
  opts?: { timeoutMs?: number; label?: string; settleMs?: number },
): Promise<HTMLElement> {
  let el: HTMLElement | null = null;
  await waitUntil(
    () => {
      el = queryVisible(selector);
      return !!el;
    },
    { timeoutMs: opts?.timeoutMs ?? 20_000, label: opts?.label || `出现 ${selector}` },
  );
  if (!el) throw new Error(`未找到元素：${opts?.label || selector}`);
  await highlightAndClick(el, { settleMs: opts?.settleMs });
  return el;
}

export async function clickByText(
  selector: string,
  text: string | RegExp,
  opts?: { timeoutMs?: number; label?: string; settleMs?: number },
): Promise<HTMLElement> {
  let el: HTMLElement | null = null;
  await waitUntil(
    () => {
      el = findByText(selector, text);
      return !!el && !el.hasAttribute('disabled') && !el.classList.contains('is-disabled');
    },
    { timeoutMs: opts?.timeoutMs ?? 20_000, label: opts?.label || `点击「${text}」` },
  );
  if (!el) throw new Error(`未找到可点击项：${opts?.label || String(text)}`);
  await highlightAndClick(el, { settleMs: opts?.settleMs });
  return el;
}

export async function setNativeInputValue(input: HTMLInputElement | HTMLTextAreaElement, value: string) {
  const proto = input.tagName === 'TEXTAREA' ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const desc = Object.getOwnPropertyDescriptor(proto, 'value');
  desc?.set?.call(input, value);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
  await sleep(120);
}

export async function assignFilesToInput(input: HTMLInputElement, files: File[]) {
  const dt = new DataTransfer();
  for (const f of files) dt.items.add(f);
  input.files = dt.files;
  input.dispatchEvent(new Event('change', { bubbles: true }));
  // Element Plus Upload 偶尔监听 input 事件
  input.dispatchEvent(new Event('input', { bubbles: true }));
  await sleep(300);
}

export async function confirmMessageBox(confirmText: string | RegExp = /确定|确认|删除|删除知识库|是/) {
  await waitUntil(
    () => !!document.querySelector('.el-message-box'),
    { timeoutMs: 8_000, label: '出现确认框' },
  );
  const btn =
    findByText('.el-message-box__btns button', confirmText)
    || queryVisible('.el-message-box__btns .el-button--primary');
  if (!btn) throw new Error('确认框中未找到确认按钮');
  await highlightAndClick(btn, { settleMs: 400 });
}

export async function waitRouteName(
  getRouteName: () => unknown,
  expected: string | string[],
  opts?: { timeoutMs?: number },
) {
  const names = Array.isArray(expected) ? expected : [expected];
  await waitUntil(
    () => names.includes(String(getRouteName() || '')),
    { timeoutMs: opts?.timeoutMs ?? 180_000, label: `路由进入 ${names.join('|')}` },
  );
}

export function unrefMaybe<T>(v: T | { value: T } | undefined | null): T | undefined {
  if (v == null) return undefined;
  if (typeof v === 'object' && v !== null && 'value' in (v as object)) {
    return (v as { value: T }).value;
  }
  return v as T;
}
